/** @jsxImportSource react */
import TalkToAgentForm from '@react/cta/components/TalkToAgentForm';
import { tocs } from '@react/search/helpers';
import { useQuery } from '@react/services/api';
import { useAuth, useUser } from '@react/services/auth';
import { eventNames, useEvents } from '@react/services/events';
import { delay } from '@react/shared/helpers';
import {
  AA_CONSULTATION_REQUESTED,
  AGENT_ASK_QUESTIONS,
  CONSULTATION_REQUESTED,
  HOME_CARE_REQUESTED,
  PROFILE_ASK_QUESTION,
} from '@sly/core/constants/actionTypes';
import {
  AGENT_ENTITY_TYPE,
  COMMUNITY_ENTITY_TYPE,
} from '@sly/core/constants/entityTypes';
import { useRouter } from 'next/router';
import { bool, func, object, oneOf, string } from 'proptypes';
import React, { useCallback } from 'react';

const AskQuestionToAgentFormContainer = (props) => {
  const createAction = useQuery('createUuidAction');
  const createQuestion = useQuery('createQuestion');
  const updateUuidAux = useQuery('updateUuidAux');

  const { asPath: url, query } = useRouter();
  const { toc } = query;

  const { createOrUpdateUser } = useAuth();

  const { events } = useEvents();

  const {
    user,
    info: {
      uuidAux: { result: rawUuidAux },
    },
  } = useUser();

  const {
    entityId,
    postSubmit,
    entityType = AGENT_ENTITY_TYPE,
    entityName,
    type,
    actionType = AGENT_ASK_QUESTIONS,
    agentSlug,
    realEstateAgentSlug,
    realEstateAgentName,
    mode,
    agentFirstName,
    realEstateAgentFirstName,
    uuidData = {},
    agentName,
  } = props;

  const handleSubmit = useCallback(
    async (data) => {
      data = {
        ...data,
        name: `${data.firstName}${data.lastName ? ` ${data.lastName}` : ''}`,
      };
      const { message, location } = data;
      let { phone, email, name } = data;

      if (user) {
        if (user.phoneNumber) ({ phoneNumber: phone } = user);
        if (user.email) ({ email } = user);
        if (user.name) ({ name } = user);
      }

      let updateUuidAuxReq = () => Promise.resolve();

      let actionInfo = {
        slug: entityId,
        entityType,
        entityName,
        name,
        email,
        phone,
        question: message,
      };

      if (agentSlug) {
        actionInfo.agentSlug = agentSlug;
      }

      if (agentName) {
        actionInfo.agentName = agentName;
      }

      if (realEstateAgentName) {
        actionInfo.realEstateAgentName = realEstateAgentName;
      }

      if (realEstateAgentSlug) {
        actionInfo.realEstateAgentSlug = realEstateAgentSlug;
      }

      if (
        actionType === CONSULTATION_REQUESTED ||
        actionType === HOME_CARE_REQUESTED ||
        actionType === AA_CONSULTATION_REQUESTED
      ) {
        actionInfo = { phone, name, email, message };
      }

      if (mode) {
        actionInfo['data'] = {
          leadSource: mode?.leadSource,
          channel: mode?.channel,
          medium: mode?.medium,
        };
      }

      if (uuidData) {
        if (actionInfo['data']) {
          actionInfo['data'] = {
            ...actionInfo['data'],
            ...uuidData,
          };
        } else {
          actionInfo['data'] = uuidData;
        }
      }

      if (location?.searchParams) {
        const { city, state, geo } = location.searchParams || {};
        if (!actionInfo['data']) {
          actionInfo['data'] = {};
        }
        if (city) {
          actionInfo.data.city = city;
        }
        if (state) {
          actionInfo.data.state = state;
        }
        if (geo) {
          actionInfo.data.geo = geo;
        }
      }

      if (actionType === CONSULTATION_REQUESTED && toc) {
        // If toc is present in query, then add it to actionData, typeCare
        if (!actionInfo['data']) {
          actionInfo['data'] = {};
        }
        tocs.forEach((tocItem) => {
          if (tocItem.value === toc) {
            actionInfo.data.typeCare = [tocItem.apiValue];
          }
        });
      }

      if (actionType === PROFILE_ASK_QUESTION) {
        // Also create a content item
        const payload = {
          communitySlug: entityId,
          question: message,
          name,
          email,
        };
        actionInfo.questionText = message; // API expects key
        return createQuestion(payload)
          .then(() => {
            Promise.all([
              createAction({
                type: 'UUIDAction',
                attributes: { actionType, actionPage: url, actionInfo },
              }),
              updateUuidAuxReq(),
            ])
              .then(() =>
                user
                  ? true
                  : createOrUpdateUser(
                      { name, email, phone },
                      { ignoreAlreadyRegistered: true }
                    )
              )
              .then(() => {
                events.track(eventNames.FormSubmitted, {
                  form: actionType,
                  location: type,
                });
                postSubmit?.(false, data);
              });
          })
          .catch(({ body }) => {
            let errorMessage;
            if (body?.errors) {
              errorMessage = body.errors[0].detail;
            } else {
              console.error(body);
            }
            console.error({ _error: errorMessage });
          });
      }
      return Promise.all([
        createAction({
          type: 'UUIDAction',
          attributes: { actionType, actionPage: url, actionInfo },
        }),
        updateUuidAuxReq(),
      ])
        .then(() =>
          user
            ? true
            : createOrUpdateUser(
                { name, email, phone },
                { ignoreAlreadyRegistered: true }
              )
        )
        .then(async () => {
          events.track(eventNames.FormSubmitted, {
            form: actionType,
            location: type,
          });

          const shouldRedirect = !user;
          if (shouldRedirect) {
            await delay(2000);
          }
          postSubmit?.(shouldRedirect, data);
        });
    },
    [
      user,
      rawUuidAux,
      entityId,
      entityType,
      agentSlug,
      actionType,
      mode,
      uuidData.adl,
      createAction,
      url,
      updateUuidAux,
      createQuestion,
      createOrUpdateUser,
      events,
      type,
      postSubmit,
      entityName,
    ]
  );

  return (
    <TalkToAgentForm
      handleSubmit={handleSubmit}
      agentFirstName={agentFirstName || realEstateAgentFirstName}
      {...{ user, ...props }}
    />
  );
};

AskQuestionToAgentFormContainer.propTypes = {
  entityId: string,
  entityName: string,
  agentSlug: string,
  postSubmit: func,
  entityType: oneOf([AGENT_ENTITY_TYPE, COMMUNITY_ENTITY_TYPE]),
  agentFirstName: string,
  messagePrompt: string,
  buttonLabel: string,
  shrinkContact: bool,
  type: string,
  mode: object,
  agentName: string,
  actionType: oneOf([
    PROFILE_ASK_QUESTION,
    AGENT_ASK_QUESTIONS,
    CONSULTATION_REQUESTED,
    HOME_CARE_REQUESTED,
  ]),
  hasLocation: bool,
};

export default AskQuestionToAgentFormContainer;
